'use client';

import { atom, useAtomValue, useSetAtom } from 'jotai';
import { useMemo } from 'react';

/**
 * 백그라운드 후처리(예: `pollCounselUntilObservingMovingConfirmed`) 가 진행 중인
 * roomObserving / additionalSpaceObserving 세션 ID 의 집합.
 *
 * `MovingItemsFlowAdapter` 가 짐+영상 저장 직후 picker 로 즉시 복귀시키면서
 * fire-and-forget 으로 백그라운드 폴링을 띄우는데, 이 시점부터 폴링 종료까지
 * 해당 sessionId 가 이 set 에 들어 있다.
 *
 * `RoomObservingFlowAdapter` 는 이 atom 을 구독해 picker 의 ItemPickerItem 에
 * `pending: true` 를 주입하고, design-system 의 `ItemCard` 가 dimmed + spinner
 * overlay + 클릭 차단을 적용한다.
 *
 * 어댑터의 mount/unmount 와 무관하게 글로벌 상태로 유지되는 게 핵심:
 * 사용자가 picker → 다른 방 진입 → 다시 돌아와도 폴링이 끝나기 전이라면
 * spinner 가 계속 보이고, 폴링이 setter 에서 제거되는 순간 picker 가 자동
 * 리렌더되어 spinner 가 사라진다.
 *
 * - Set 의 reference identity 는 매 update 마다 새로 만든다 (`new Set(prev)`).
 *   atom 구독이 reference 비교로 리렌더를 결정하므로 mutate 가 아닌 replace
 *   패턴을 유지한다.
 * - 빈 set 은 모듈 scope 의 EMPTY_SET 으로 fallback 해 reference 안정성 확보.
 */
const pendingObservingSessionIdsAtom = atom<ReadonlySet<string>>(new Set<string>());

const EMPTY_SET: ReadonlySet<string> = new Set<string>();

/** picker 어댑터에서 구독용. 빈 상태는 모듈 EMPTY_SET 으로 안정 reference 반환 */
export function usePendingObservingSessionIds(): ReadonlySet<string> {
  const value = useAtomValue(pendingObservingSessionIdsAtom);
  return value.size === 0 ? EMPTY_SET : value;
}

export interface ObservingSessionPendingActions {
  markPending: (sessionId: string) => void;
  clearPending: (sessionId: string) => void;
}

/**
 * `MovingItemsFlowAdapter.handleComplete` 가 백그라운드 폴링 시작/종료에
 * 맞춰 호출하기 위한 actions.
 *
 * - markPending: 폴링 시작 직전.
 * - clearPending: 폴링 결과(then/catch/finally) 시.
 *
 * 두 함수 모두 안정 reference 를 가져 useEffect deps 에 안전하게 사용 가능.
 */
export function useObservingSessionPendingActions(): ObservingSessionPendingActions {
  const setIds = useSetAtom(pendingObservingSessionIdsAtom);
  return useMemo<ObservingSessionPendingActions>(
    () => ({
      markPending: (sessionId) => {
        setIds((prev) => {
          if (prev.has(sessionId)) return prev;
          const next = new Set(prev);
          next.add(sessionId);
          return next;
        });
      },
      clearPending: (sessionId) => {
        setIds((prev) => {
          if (!prev.has(sessionId)) return prev;
          const next = new Set(prev);
          next.delete(sessionId);
          return next;
        });
      },
    }),
    [setIds]
  );
}
